import * as React from 'react'

import type {BuildTypeId, ProjectId} from '../../../../types'
import type {CompareBuildsLine} from '../CompareBuildsPage.types'

import CountRow from './CountRow/CountRow'
import DependencyRow from './DependencyRow/DependencyRow'
import ParameterRow from './ParameterRow/ParameterRow'
import RevisionRow from './RevisionRow/RevisionRow'
import TestRow from './TestRow/TestRow'
import TitleRow from './TitleRow/TitleRow'
import UnknownRow from './UnknownRow/UnknownRow'

type Props = {
  line: CompareBuildsLine
  showChangedOnly: boolean
  buildTypeId: BuildTypeId | null | undefined
  sourceProjectId: ProjectId | null | undefined
  compareProjectId: ProjectId | null | undefined
  sourceBuildNumber: string | null | undefined
  compareBuildNumber: string | null | undefined
  wide: boolean
}

function CompareBuildsRow({
  line,
  showChangedOnly,
  buildTypeId,
  sourceProjectId,
  compareProjectId,
  sourceBuildNumber,
  compareBuildNumber,
  wide,
}: Props) {
  switch (line.type) {
    case 'title':
      return <TitleRow name={line.name} level={line.level} hasChildren={line.hasChildren} />

    case 'dependency':
      return (
        <DependencyRow
          buildTypeId={line.data1?.buildTypeId}
          buildTypeName={line.data1?.buildTypeName ?? line.data2?.buildTypeName}
          buildId1={line.data1?.buildId}
          buildNumber1={line.data1?.buildNumber}
          isCanceled1={line.data1?.canceled ?? false}
          isPersonal1={line.data1?.personal ?? false}
          state1={line.data1?.state ?? 'finished'}
          buildId2={line.data2?.buildId}
          buildNumber2={line.data2?.buildNumber}
          isCanceled2={line.data2?.canceled ?? false}
          isPersonal2={line.data2?.personal ?? false}
          state2={line.data2?.state ?? 'finished'}
          changed={!showChangedOnly && line.changed}
        />
      )

    case 'param':
      return (
        <ParameterRow
          name1={line.data1?.name}
          start1={line.data1?.start}
          finish1={line.data1?.finish}
          name2={line.data2?.name}
          start2={line.data2?.start}
          finish2={line.data2?.finish}
          changed={!showChangedOnly && line.changed}
        />
      )

    case 'revision':
      return (
        <RevisionRow
          buildTypeId={buildTypeId}
          branch1={line.data1?.branch}
          hash1={line.data1?.hash}
          vcsRootName1={line.data1?.vcsRootName}
          branch2={line.data2?.branch}
          hash2={line.data2?.hash}
          vcsRootName2={line.data2?.vcsRootName}
          changed={!showChangedOnly && line.changed}
        />
      )

    case 'test': {
      const testExistsInSource = line.data1?.name != null
      return (
        <TestRow
          name={testExistsInSource ? line.data1?.name : line.data2?.name}
          suite={testExistsInSource ? line.data1?.suite : line.data2?.suite}
          testNameId={testExistsInSource ? line.data1?.testNameId : line.data2?.testNameId}
          projectId={testExistsInSource ? sourceProjectId : compareProjectId}
          status1={line.data1?.status}
          status2={line.data2?.status}
          duration1={line.data1?.duration}
          duration2={line.data2?.duration}
          muted1={line.data1?.muted}
          muted2={line.data2?.muted}
          changed={!showChangedOnly && line.changed}
          sourceBuildNumber={sourceBuildNumber}
          compareBuildNumber={compareBuildNumber}
          wide={wide}
        />
      )
    }

    case 'count':
      return (
        <CountRow
          count1={line.data1?.count}
          count2={line.data2?.count}
          changed={!showChangedOnly && line.changed}
        />
      )

    default:
      const unknownLine: any = line
      return (
        <UnknownRow
          data1={unknownLine.data1}
          data2={unknownLine.data2}
          changed={!showChangedOnly && unknownLine.changed}
        />
      )
  }
}

export default React.memo<Props>(CompareBuildsRow)
